/**
 * Custom Fields Management Page
 * Issue #162: Add Custom Fields Configuration
 *
 * Allows admins to define custom fields for entities (risks, controls, vendors, etc.)
 */

import { useState, useEffect } from 'react'
import { api, CustomFieldDefinition, CreateCustomFieldInput, UpdateCustomFieldInput } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  GripVertical,
  AlertCircle,
  CheckCircle,
  Loader2,
  Settings,
  Type,
  Hash,
  Calendar,
  ChevronDown,
  ToggleLeft,
  FileText,
  Mail,
  Link,
  HelpCircle,
} from 'lucide-react'

type EntityType = 'risk' | 'control' | 'vendor' | 'evidence' | 'document'
type FieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea' | 'email' | 'url'

const ENTITY_TYPES: { value: EntityType; label: string; icon: any }[] = [
  { value: 'risk', label: 'Risks', icon: AlertCircle },
  { value: 'control', label: 'Controls', icon: Settings },
  { value: 'vendor', label: 'Vendors', icon: FileText },
  { value: 'evidence', label: 'Evidence', icon: FileText },
  { value: 'document', label: 'Documents', icon: FileText },
]

const FIELD_TYPES: { value: FieldType; label: string; icon: any; description: string }[] = [
  { value: 'text', label: 'Text', icon: Type, description: 'Short text input' },
  { value: 'textarea', label: 'Text Area', icon: FileText, description: 'Multi-line text' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric value' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { value: 'select', label: 'Dropdown', icon: ChevronDown, description: 'Select from options' },
  { value: 'checkbox', label: 'Checkbox', icon: ToggleLeft, description: 'Yes/No toggle' },
  { value: 'email', label: 'Email', icon: Mail, description: 'Email address' },
  { value: 'url', label: 'URL', icon: Link, description: 'Web link' },
]

interface FieldFormState {
  entity_type: EntityType
  field_name: string
  field_label: string
  field_type: FieldType
  options: Array<{ value: string; label: string }>
  required: boolean
  default_value: string
  min_value: string
  max_value: string
  max_length: string
  placeholder: string
  help_text: string
  show_in_list: boolean
}

const INITIAL_FORM_STATE: FieldFormState = {
  entity_type: 'risk',
  field_name: '',
  field_label: '',
  field_type: 'text',
  options: [],
  required: false,
  default_value: '',
  min_value: '',
  max_value: '',
  max_length: '',
  placeholder: '',
  help_text: '',
  show_in_list: false,
}

export default function CustomFields() {
  const { user } = useAuth()
  const toast = useToast()
  const confirmDialog = useConfirm()

  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>('risk')
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingField, setEditingField] = useState<CustomFieldDefinition | null>(null)
  const [formState, setFormState] = useState<FieldFormState>(INITIAL_FORM_STATE)
  const [newOption, setNewOption] = useState({ value: '', label: '' })
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Check admin access
  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Admin Access Required</h2>
        <p className="text-gray-600">Only administrators can manage custom fields.</p>
      </div>
    )
  }

  useEffect(() => {
    fetchDefinitions()
  }, [selectedEntityType])

  async function fetchDefinitions() {
    setLoading(true)
    try {
      const res = await api.getCustomFieldDefinitions(selectedEntityType, false)
      if (res.success) {
        setDefinitions(res.definitions)
      }
    } catch (err) {
      console.error('Failed to load custom fields:', err)
      showMessage('error', 'Failed to load custom fields')
    } finally {
      setLoading(false)
    }
  }

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 4000)
  }

  function openCreateModal() {
    setEditingField(null)
    setFormState({
      ...INITIAL_FORM_STATE,
      entity_type: selectedEntityType,
    })
    setNewOption({ value: '', label: '' })
    setShowModal(true)
  }

  function openEditModal(field: CustomFieldDefinition) {
    setEditingField(field)
    setFormState({
      entity_type: field.entity_type,
      field_name: field.field_name,
      field_label: field.field_label,
      field_type: field.field_type,
      options: field.options || [],
      required: field.required,
      default_value: field.default_value || '',
      min_value: field.min_value?.toString() || '',
      max_value: field.max_value?.toString() || '',
      max_length: field.max_length?.toString() || '',
      placeholder: field.placeholder || '',
      help_text: field.help_text || '',
      show_in_list: field.show_in_list,
    })
    setNewOption({ value: '', label: '' })
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditingField(null)
    setFormState(INITIAL_FORM_STATE)
    setNewOption({ value: '', label: '' })
  }

  // Generate field_name from field_label
  function generateFieldName(label: string): string {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 50)
  }

  async function handleSubmit() {
    // Validate
    if (!formState.field_label.trim()) {
      showMessage('error', 'Field label is required')
      return
    }

    if (formState.field_type === 'select' && formState.options.length === 0) {
      showMessage('error', 'Dropdown fields require at least one option')
      return
    }

    setSaving(true)
    try {
      if (editingField) {
        // Update existing
        const updateData: UpdateCustomFieldInput = {
          field_label: formState.field_label,
          options: formState.field_type === 'select' ? formState.options : undefined,
          required: formState.required,
          default_value: formState.default_value || null,
          min_value: formState.min_value ? parseFloat(formState.min_value) : null,
          max_value: formState.max_value ? parseFloat(formState.max_value) : null,
          max_length: formState.max_length ? parseInt(formState.max_length) : null,
          placeholder: formState.placeholder || null,
          help_text: formState.help_text || null,
          show_in_list: formState.show_in_list,
        }

        const res = await api.updateCustomFieldDefinition(editingField.id, updateData)
        if (res.success) {
          showMessage('success', 'Custom field updated')
          closeModal()
          fetchDefinitions()
        } else {
          showMessage('error', 'Failed to update field')
        }
      } else {
        // Create new
        const fieldName = formState.field_name || generateFieldName(formState.field_label)

        if (!fieldName || !/^[a-z][a-z0-9_]*$/.test(fieldName)) {
          showMessage('error', 'Field name must start with a letter and contain only lowercase letters, numbers, and underscores')
          return
        }

        const createData: CreateCustomFieldInput = {
          entity_type: formState.entity_type,
          field_name: fieldName,
          field_label: formState.field_label,
          field_type: formState.field_type,
          options: formState.field_type === 'select' ? formState.options : undefined,
          required: formState.required,
          default_value: formState.default_value || undefined,
          min_value: formState.min_value ? parseFloat(formState.min_value) : undefined,
          max_value: formState.max_value ? parseFloat(formState.max_value) : undefined,
          max_length: formState.max_length ? parseInt(formState.max_length) : undefined,
          placeholder: formState.placeholder || undefined,
          help_text: formState.help_text || undefined,
          show_in_list: formState.show_in_list,
        }

        const res = await api.createCustomFieldDefinition(createData)
        if (res.success) {
          showMessage('success', 'Custom field created')
          closeModal()
          fetchDefinitions()
        } else {
          showMessage('error', 'Failed to create field')
        }
      }
    } catch (err: any) {
      showMessage('error', err.message || 'An error occurred')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(field: CustomFieldDefinition) {
    const confirmed = await confirmDialog({
      title: 'Delete Custom Field',
      message: `Are you sure you want to delete "${field.field_label}"? This will also delete all saved values for this field across all ${field.entity_type}s.`,
      type: 'danger',
      confirmText: 'Delete',
    })

    if (!confirmed) return

    try {
      const res = await api.deleteCustomFieldDefinition(field.id)
      if (res.success) {
        showMessage('success', 'Custom field deleted')
        fetchDefinitions()
      } else {
        showMessage('error', 'Failed to delete field')
      }
    } catch (err) {
      showMessage('error', 'Failed to delete field')
    }
  }

  async function handleToggleActive(field: CustomFieldDefinition) {
    try {
      const res = await api.updateCustomFieldDefinition(field.id, { active: !field.active })
      if (res.success) {
        fetchDefinitions()
      }
    } catch (err) {
      showMessage('error', 'Failed to update field')
    }
  }

  function addOption() {
    if (!newOption.value.trim() || !newOption.label.trim()) return

    // Check for duplicate values
    if (formState.options.some(opt => opt.value === newOption.value.trim())) {
      showMessage('error', 'Option value already exists')
      return
    }

    setFormState({
      ...formState,
      options: [...formState.options, { value: newOption.value.trim(), label: newOption.label.trim() }],
    })
    setNewOption({ value: '', label: '' })
  }

  function removeOption(index: number) {
    setFormState({
      ...formState,
      options: formState.options.filter((_, i) => i !== index),
    })
  }

  const getFieldTypeInfo = (type: FieldType) => FIELD_TYPES.find(t => t.value === type)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Fields</h1>
          <p className="text-gray-500">Define custom data fields for your entities</p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Custom Field
        </button>
      </div>

      {/* Message Toast */}
      {message && (
        <div
          className={`flex items-center gap-2 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          {message.text}
        </div>
      )}

      {/* Entity Type Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {ENTITY_TYPES.map((entity) => (
            <button
              key={entity.value}
              onClick={() => setSelectedEntityType(entity.value)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                selectedEntityType === entity.value
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <entity.icon className="h-4 w-4" />
              {entity.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Fields List */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-gray-400" />
            <p className="text-gray-500 mt-2">Loading custom fields...</p>
          </div>
        ) : definitions.length === 0 ? (
          <div className="p-8 text-center">
            <Settings className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No custom fields defined for {ENTITY_TYPES.find(e => e.value === selectedEntityType)?.label}</p>
            <p className="text-sm text-gray-400 mt-1">Click "Add Custom Field" to create one.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {definitions.map((field) => {
              const typeInfo = getFieldTypeInfo(field.field_type)
              const TypeIcon = typeInfo?.icon || Type

              return (
                <div
                  key={field.id}
                  className={`p-4 flex items-center gap-4 ${!field.active ? 'bg-gray-50 opacity-70' : ''}`}
                >
                  <div className="flex-shrink-0 text-gray-400 cursor-move">
                    <GripVertical className="h-5 w-5" />
                  </div>

                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      field.active ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-500'
                    }`}>
                      <TypeIcon className="h-5 w-5" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{field.field_label}</span>
                      {field.required && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                      )}
                      {field.show_in_list && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">In List</span>
                      )}
                      {!field.active && (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">Disabled</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded mr-2">{field.field_name}</code>
                      <span>{typeInfo?.label}</span>
                      {field.field_type === 'select' && field.options && (
                        <span className="ml-2 text-gray-400">({field.options.length} options)</span>
                      )}
                    </div>
                    {field.help_text && (
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <HelpCircle className="h-3 w-3" />
                        {field.help_text}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(field)}
                      className={`text-xs px-2 py-1 rounded ${
                        field.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {field.active ? 'Active' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => openEditModal(field)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
                      title="Edit field"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(field)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                      title="Delete field"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingField ? 'Edit Custom Field' : 'Create Custom Field'}
                  </h3>
                  <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                  {/* Entity Type (read-only when editing) */}
                  <div>
                    <label className="label">Entity Type</label>
                    <select
                      value={formState.entity_type}
                      onChange={(e) => setFormState({ ...formState, entity_type: e.target.value as EntityType })}
                      className="input"
                      disabled={!!editingField}
                    >
                      {ENTITY_TYPES.map((et) => (
                        <option key={et.value} value={et.value}>{et.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Field Label */}
                  <div>
                    <label className="label">Field Label *</label>
                    <input
                      type="text"
                      value={formState.field_label}
                      onChange={(e) => {
                        const label = e.target.value
                        setFormState({
                          ...formState,
                          field_label: label,
                          field_name: editingField ? formState.field_name : generateFieldName(label),
                        })
                      }}
                      className="input"
                      placeholder="e.g., Business Unit"
                    />
                  </div>

                  {/* Field Name (read-only when editing) */}
                  <div>
                    <label className="label">Field Name (API identifier)</label>
                    <input
                      type="text"
                      value={formState.field_name}
                      onChange={(e) => setFormState({ ...formState, field_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                      className="input font-mono text-sm"
                      placeholder="business_unit"
                      disabled={!!editingField}
                    />
                    <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only</p>
                  </div>

                  {/* Field Type (read-only when editing) */}
                  <div>
                    <label className="label">Field Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {FIELD_TYPES.map((ft) => (
                        <button
                          key={ft.value}
                          type="button"
                          onClick={() => !editingField && setFormState({ ...formState, field_type: ft.value })}
                          disabled={!!editingField}
                          className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors ${
                            formState.field_type === ft.value
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          } ${editingField ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <ft.icon className="h-4 w-4" />
                          <div>
                            <div className="text-sm font-medium">{ft.label}</div>
                            <div className="text-xs text-gray-500">{ft.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Options for Select type */}
                  {formState.field_type === 'select' && (
                    <div>
                      <label className="label">Dropdown Options *</label>
                      <div className="space-y-2">
                        {formState.options.map((opt, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={opt.label}
                              readOnly
                              className="input flex-1 bg-gray-50 text-sm"
                            />
                            <code className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {opt.value}
                            </code>
                            <button
                              type="button"
                              onClick={() => removeOption(index)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newOption.label}
                            onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                            className="input flex-1"
                            placeholder="Display label"
                          />
                          <input
                            type="text"
                            value={newOption.value}
                            onChange={(e) => setNewOption({ ...newOption, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                            className="input w-32 font-mono text-sm"
                            placeholder="value"
                          />
                          <button
                            type="button"
                            onClick={addOption}
                            className="btn-secondary px-3"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Number constraints */}
                  {formState.field_type === 'number' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Minimum Value</label>
                        <input
                          type="number"
                          value={formState.min_value}
                          onChange={(e) => setFormState({ ...formState, min_value: e.target.value })}
                          className="input"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="label">Maximum Value</label>
                        <input
                          type="number"
                          value={formState.max_value}
                          onChange={(e) => setFormState({ ...formState, max_value: e.target.value })}
                          className="input"
                          placeholder="100"
                        />
                      </div>
                    </div>
                  )}

                  {/* Text constraints */}
                  {(formState.field_type === 'text' || formState.field_type === 'textarea') && (
                    <div>
                      <label className="label">Maximum Length</label>
                      <input
                        type="number"
                        value={formState.max_length}
                        onChange={(e) => setFormState({ ...formState, max_length: e.target.value })}
                        className="input w-32"
                        placeholder="255"
                      />
                    </div>
                  )}

                  {/* Default Value */}
                  <div>
                    <label className="label">Default Value</label>
                    {formState.field_type === 'checkbox' ? (
                      <select
                        value={formState.default_value}
                        onChange={(e) => setFormState({ ...formState, default_value: e.target.value })}
                        className="input"
                      >
                        <option value="">Unchecked</option>
                        <option value="1">Checked</option>
                      </select>
                    ) : formState.field_type === 'select' ? (
                      <select
                        value={formState.default_value}
                        onChange={(e) => setFormState({ ...formState, default_value: e.target.value })}
                        className="input"
                      >
                        <option value="">No default</option>
                        {formState.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={formState.field_type === 'number' ? 'number' : formState.field_type === 'date' ? 'date' : 'text'}
                        value={formState.default_value}
                        onChange={(e) => setFormState({ ...formState, default_value: e.target.value })}
                        className="input"
                        placeholder="Optional default value"
                      />
                    )}
                  </div>

                  {/* Placeholder */}
                  {formState.field_type !== 'checkbox' && formState.field_type !== 'select' && (
                    <div>
                      <label className="label">Placeholder Text</label>
                      <input
                        type="text"
                        value={formState.placeholder}
                        onChange={(e) => setFormState({ ...formState, placeholder: e.target.value })}
                        className="input"
                        placeholder="Enter placeholder text..."
                      />
                    </div>
                  )}

                  {/* Help Text */}
                  <div>
                    <label className="label">Help Text</label>
                    <input
                      type="text"
                      value={formState.help_text}
                      onChange={(e) => setFormState({ ...formState, help_text: e.target.value })}
                      className="input"
                      placeholder="Additional guidance for users"
                    />
                  </div>

                  {/* Toggles */}
                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Required Field</p>
                        <p className="text-xs text-gray-500">Users must fill in this field</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formState.required}
                        onChange={(e) => setFormState({ ...formState, required: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Show in List View</p>
                        <p className="text-xs text-gray-500">Display as a column in the main list</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formState.show_in_list}
                        onChange={(e) => setFormState({ ...formState, show_in_list: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="btn-primary w-full sm:ml-3 sm:w-auto"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingField ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingField ? 'Update Field' : 'Create Field'}
                    </>
                  )}
                </button>
                <button onClick={closeModal} className="btn-secondary mt-3 w-full sm:mt-0 sm:w-auto">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
